import { axios } from "@tlc/ls2pac/components/AxiosInstanceManager";
import { SearchParametersDwr } from "../ls/LSSearch";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLoaderData, useSearchParams } from "react-router-dom";
import { LoaderData } from "../../account/MyAccount";
import { EbscoResultsPageLoader } from "./EbscoSearchResults";
import { EbscoResultRecord } from "./EbscoResultRecord";

interface EbscoGetRecordsResponse {
  success: boolean;
  totalHits: number;
  results: EbscoResultRecord[];
}

interface EbscoSearchContextType {
  setSort: (sort: string) => void;
  /**
   * @param page 1 based page index
   */
  setPage: (page: number) => void;
  searchParams: { page: number; sort: string; term: string };
  totalHits: Readonly<number | null>;
  results: Readonly<EbscoResultRecord[] | null>;
  resultsKey: Readonly<string | null>;
  loadingResults: Readonly<boolean>;
  pageSize: number;
}

const EbscoSearchContext = createContext<EbscoSearchContextType | undefined>(
  undefined
);

export const useEbscoSearch = () => {
  const ctx = useContext(EbscoSearchContext);
  if (!ctx)
    throw new Error("useEbscoSearch must be used within EbscoSearchProvider");
  return ctx;
};

function getEbscoResultRecordsKey(results: EbscoResultRecord[]) {
  if (!results) {
    return null;
  }
  return results
    .map((r) => r.recordId)
    .sort()
    .toString();
}

export const EbscoSearchProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { sortOptions } = useLoaderData() as LoaderData<
    typeof EbscoResultsPageLoader
  >;
  const [searchParams, setSearchParams] = useSearchParams();
  const [loadingResults, setLoadingResults] = useState<boolean>(false);
  const [totalHits, setTotalHits] = useState<number | null>(null);
  const [results, setResults] = useState<EbscoResultRecord[] | null>(null);
  const resultsKey = useMemo(
    () => getEbscoResultRecordsKey(results),
    [results]
  );

  const state = useMemo(() => {
    const term = searchParams.get("term") || "";
    const sort = searchParams.has("sort")
      ? searchParams.get("sort")
      : sortOptions[0].id;

    const pageParam = parseInt(searchParams.get("page") || "1", 10);
    const page = pageParam < 1 ? 1 : pageParam;
    return {
      term,
      page,
      sort,
    };
  }, [searchParams]);

  useEffect(() => {
    if (!state) {
      return;
    }
    setLoadingResults(true);
    const { term, page, sort } = state;
    search(term, page, sort).then((response) => {
      // TODO set count on parent count provider
      setTotalHits(response.totalHits);
      setResults(response.results);
      setLoadingResults(false);
    });
  }, [state.page, state.sort, state.term]);

  const pageSize = 12;

  const search = async (term: string, page: number = 1, sort: string) => {
    // Ebsco uses 1 based index...
    const startIndex = page <= 1 ? 1 : (page - 1) * pageSize + 1;
    const payload: SearchParametersDwr = {
      searchTerm: term,
      hitsPerPage: pageSize,
      sortCriteria: sort,
      startIndex,
    };

    try {
      const { data } = await axios.post<EbscoGetRecordsResponse>(
        `/ebsco/search`,
        payload
      );
      return data;
    } catch (e: unknown) {
      return {
        success: false,
        totalHits: 0,
        results: [],
      };
    }
  };

  const setPage = (page: number) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (page <= 1) {
        newParams.delete("page");
      } else {
        newParams.set("page", page.toString());
      }
      return newParams;
    });
  };

  const setSort = (sort: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("sort", sort);
      newParams.delete("page");
      return newParams;
    });
  };

  const contextValue: EbscoSearchContextType = useMemo(() => {
    return {
      searchParams: state,
      setSort,
      setPage,
      totalHits,
      results,
      resultsKey,
      loadingResults,
      pageSize,
    };
  }, [state, totalHits, loadingResults, resultsKey]);

  return (
    <EbscoSearchContext.Provider value={contextValue}>
      {children}
    </EbscoSearchContext.Provider>
  );
};
